import { useEffect, useRef, useState } from "react";
import type { TokenGetter } from "../lib/api";
import { createPayment } from "../lib/api";
import {
  paymentInProgressDetail,
  paymentProviderUnavailableDetail,
  reviewRequiredDetail,
  type ReviewRequiredDetail,
} from "../lib/apiError";
import { PayMethodDivider } from "./OfficialPayButtons";

/**
 * Documented `@revolut/checkout` ButtonStyleOptions only — no custom
 * colours. Apple Pay / Google Pay / Revolut Pay trademarks forbid painting
 * a coral fake. `variant: "dark"` is the official black button on a light
 * page (matches Apple HIG / Google brand). Height 48px = our `size="lg"`
 * card CTA (`h-12`).
 */
export const REVOLUT_WALLET_BUTTON_STYLE = {
  height: "48px",
  size: "large",
  radius: "small",
  variant: "dark",
  action: "pay",
} as const;

const walletSlotClass = "block h-12 w-full [&>*]:block [&>*]:h-12 [&>*]:w-full";

/**
 * Revolut Pay + Apple Pay + Google Pay via `RevolutCheckout.payments`.
 *
 * Separate from the card `payWithPopup` path: wallets need the merchant
 * public key and create the Revolut order lazily in `createOrder` (still
 * `POST /api/payments` — same reservation / quote pin / amount / 409
 * review_required path). The checkout token is single-use and is never
 * cached; only `payment_id` is handed to the parent on success so
 * `POST /api/orders` can run after Revolut reports completed/authorised.
 *
 * Apple/Google buttons stay hidden until `canMakePayment()` confirms the
 * method. Revolut Pay always mounts when this component is rendered.
 */
export function RevolutWalletButtons({
  mode,
  publicToken,
  quoteId,
  qty,
  totalCents,
  getToken,
  disabled,
  onPaid,
  onError,
  onCancel,
  onReviewRequired,
  onPaymentInProgress,
  onBusy,
}: {
  mode: "sandbox" | "prod";
  publicToken: string;
  quoteId: string;
  qty: number;
  totalCents: number;
  getToken: TokenGetter;
  disabled: boolean;
  onPaid: (paymentId: string) => void;
  onError: (message: string) => void;
  onCancel: () => void;
  onReviewRequired: (detail: ReviewRequiredDetail) => void;
  /** Fresh lock on this quote — do not auto-release (FSEC-1 / #52). */
  onPaymentInProgress: (message: string) => void;
  onBusy: (busy: boolean) => void;
}) {
  const revolutPayRef = useRef<HTMLDivElement>(null);
  const appleRef = useRef<HTMLDivElement>(null);
  const googleRef = useRef<HTMLDivElement>(null);

  const [showApple, setShowApple] = useState(false);
  const [showGoogle, setShowGoogle] = useState(false);

  // Latest props for SDK callbacks (avoid remounting on every render).
  const qtyRef = useRef(qty);
  qtyRef.current = qty;
  const quoteIdRef = useRef(quoteId);
  quoteIdRef.current = quoteId;
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const onPaidRef = useRef(onPaid);
  onPaidRef.current = onPaid;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;
  const onReviewRequiredRef = useRef(onReviewRequired);
  onReviewRequiredRef.current = onReviewRequired;
  const onPaymentInProgressRef = useRef(onPaymentInProgress);
  onPaymentInProgressRef.current = onPaymentInProgress;
  const onBusyRef = useRef(onBusy);
  onBusyRef.current = onBusy;

  useEffect(() => {
    const revolutEl = revolutPayRef.current;
    const appleEl = appleRef.current;
    const googleEl = googleRef.current;
    if (!revolutEl || !appleEl || !googleEl) return;

    let cancelled = false;
    let reviewFlipped = false;
    // Token is returned to the SDK and forgotten; only payment_id is kept
    // long enough to place the order after a successful capture.
    let pendingPaymentId: string | null = null;
    let paymentsDestroy: (() => void) | undefined;
    let revolutPayDestroy: (() => void) | undefined;
    let appleDestroy: (() => void) | undefined;
    let googleDestroy: (() => void) | undefined;

    function forgetPending(): void {
      pendingPaymentId = null;
    }

    async function createWalletOrder(): Promise<{ publicId: string }> {
      onBusyRef.current(true);
      try {
        const session = await createPayment(
          { quote_id: quoteIdRef.current, qty: qtyRef.current },
          getTokenRef.current,
        );
        if (session.mode === "stub" || !session.token) {
          throw new Error("Wallet checkout needs a live Revolut session");
        }
        // Do not cache the token. The SDK consumes it once; a retry must
        // call POST /api/payments again (tokens are single-use).
        pendingPaymentId = session.payment_id;
        return { publicId: session.token };
      } catch (err) {
        const review = reviewRequiredDetail(err);
        const inProgress = paymentInProgressDetail(err);
        if (review) {
          reviewFlipped = true;
          onReviewRequiredRef.current(review);
          onBusyRef.current(false);
        } else if (inProgress) {
          // Another tab (or a leftover lock) holds the quote. Parent must
          // NOT auto-release — that would re-open two-tab double-charge.
          onPaymentInProgressRef.current(
            inProgress.message ||
              "A payment for this quote is already in progress.",
          );
          onBusyRef.current(false);
        } else {
          const providerDown = paymentProviderUnavailableDetail(err);
          onErrorRef.current(
            providerDown
              ? providerDown.message
              : err instanceof Error
                ? err.message
                : String(err),
          );
          onBusyRef.current(false);
        }
        throw err instanceof Error ? err : new Error(String(err));
      }
    }

    function handlePaid(): void {
      const paymentId = pendingPaymentId;
      forgetPending();
      if (!paymentId) {
        onErrorRef.current("Payment succeeded but the payment reference was lost");
        onBusyRef.current(false);
        return;
      }
      onPaidRef.current(paymentId);
    }

    function handleError(message: string): void {
      if (reviewFlipped) return;
      forgetPending();
      onErrorRef.current(message);
    }

    function handleCancel(): void {
      if (reviewFlipped) return;
      forgetPending();
      onCancelRef.current();
    }

    void (async () => {
      const { default: RevolutCheckout } = await import("@revolut/checkout");
      if (cancelled) return;

      const payments = await RevolutCheckout.payments({
        publicToken,
        mode,
        locale: "auto",
      });
      if (cancelled) {
        payments.destroy();
        return;
      }
      paymentsDestroy = () => payments.destroy();

      payments.revolutPay.mount(revolutEl, {
        currency: "EUR",
        totalAmount: totalCents,
        createOrder: createWalletOrder,
        buttonStyle: { ...REVOLUT_WALLET_BUTTON_STYLE },
      });
      payments.revolutPay.on("payment", (payload) => {
        if (payload.type === "success") {
          handlePaid();
          return;
        }
        if (payload.type === "error") {
          handleError(payload.error.message || "Payment failed");
          return;
        }
        handleCancel();
      });
      revolutPayDestroy = () => payments.revolutPay.destroy();

      const apple = payments.paymentRequest(appleEl, {
        amount: totalCents,
        currency: "EUR",
        preferredPaymentMethod: "applePay",
        createOrder: createWalletOrder,
        buttonStyle: { ...REVOLUT_WALLET_BUTTON_STYLE },
        onSuccess: handlePaid,
        onError: (err) => handleError(err.message || "Payment failed"),
        onCancel: handleCancel,
      });
      appleDestroy = () => apple.destroy();

      const google = payments.paymentRequest(googleEl, {
        amount: totalCents,
        currency: "EUR",
        preferredPaymentMethod: "googlePay",
        createOrder: createWalletOrder,
        buttonStyle: { ...REVOLUT_WALLET_BUTTON_STYLE },
        onSuccess: handlePaid,
        onError: (err) => handleError(err.message || "Payment failed"),
        onCancel: handleCancel,
      });
      googleDestroy = () => google.destroy();

      const [appleMethod, googleMethod] = await Promise.all([
        apple.canMakePayment(),
        google.canMakePayment(),
      ]);
      if (cancelled) return;

      if (appleMethod === "applePay") {
        await apple.render();
        if (!cancelled) setShowApple(true);
      } else {
        apple.destroy();
        appleDestroy = undefined;
      }

      if (googleMethod === "googlePay") {
        await google.render();
        if (!cancelled) setShowGoogle(true);
      } else {
        google.destroy();
        googleDestroy = undefined;
      }
    })();

    return () => {
      cancelled = true;
      setShowApple(false);
      setShowGoogle(false);
      appleDestroy?.();
      googleDestroy?.();
      revolutPayDestroy?.();
      paymentsDestroy?.();
    };
  }, [mode, publicToken, totalCents]);

  return (
    <div
      className={`flex w-full flex-col gap-3${disabled ? " pointer-events-none opacity-60" : ""}`}
      data-testid="order-wallets"
    >
      <div
        ref={appleRef}
        className={walletSlotClass}
        data-testid="order-apple-pay"
        hidden={!showApple}
      />
      <div
        ref={googleRef}
        className={walletSlotClass}
        data-testid="order-google-pay"
        hidden={!showGoogle}
      />
      <div
        ref={revolutPayRef}
        className={walletSlotClass}
        data-testid="order-revolut-pay"
      />
      <PayMethodDivider />
    </div>
  );
}
